import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './PlaceAutocomplete.css';

/**
 * PlaceAutocomplete - Using the NEW Google Places API (2025+)
 *
 * This component uses the new Places API:
 * - AutocompleteSuggestion.fetchAutocompleteSuggestions() for predictions
 * - PlacePrediction.toPlace().fetchFields() for place details
 *
 * @see https://developers.google.com/maps/documentation/javascript/place-autocomplete-data
 * @see https://developers.google.com/maps/documentation/javascript/legacy/places-migration-autocomplete
 */

export interface PlaceAutocompleteResult {
  formattedAddress: string;
  displayName: string;
  location: {
    lat: number;
    lng: number;
  } | null;
  placeId: string;
  types?: string[];
  addressComponents?: google.maps.places.AddressComponent[];
}

export interface PlaceAutocompleteProps {
  /** Placeholder text for the input */
  placeholder?: string;
  /** Current value of the input (for controlled mode) */
  value?: string;
  /** Callback when value changes (typing) */
  onChange?: (value: string) => void;
  /** Callback when a place is selected from suggestions */
  onPlaceSelect?: (result: PlaceAutocompleteResult) => void;
  /** Callback when the autocomplete element is ready */
  onLoad?: () => void;
  /** Types of places to show (e.g., 'geocode', 'establishment') */
  types?: string[];
  /** Country restriction (e.g., 'fr', 'us') */
  countryRestriction?: string | string[];
  /** Whether the input is disabled */
  disabled?: boolean;
  /** CSS class name for the container */
  className?: string;
  /** Error message to display */
  error?: string;
  /** Icon name from Design System */
  icon?: string;
}

interface Prediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
  types: string[];
  // Store the original suggestion for fetching details
  originalSuggestion: google.maps.places.PlacePrediction;
}

// Extend window to include Google Maps types
declare global {
  interface Window {
    google: typeof google;
  }
}

/**
 * PlaceAutocomplete component using NEW Places API (AutocompleteSuggestion)
 */
export const PlaceAutocomplete: React.FC<PlaceAutocompleteProps> = ({
  placeholder = 'Search for a place...',
  value = '',
  onChange,
  onPlaceSelect,
  onLoad,
  types,
  countryRestriction,
  disabled = false,
  className = '',
  error,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize Google Places API (new)
  useEffect(() => {
    const initPlacesAPI = async () => {
      try {
        if (!window.google?.maps) {
          console.warn('🗺️ [PlaceAutocomplete] Google Maps not loaded yet, waiting...');
          setTimeout(initPlacesAPI, 100);
          return;
        }

        // Import the new places library
        await window.google.maps.importLibrary('places');

        // Check if the new API is available
        if (typeof google.maps.places.AutocompleteSuggestion === 'undefined') {
          console.error('❌ [PlaceAutocomplete] AutocompleteSuggestion not available. Make sure Places API (New) is enabled.');
          setIsLoading(false);
          return;
        }

        console.log('✅ [PlaceAutocomplete] New Places API ready (AutocompleteSuggestion)');
        setApiReady(true);
        setIsLoading(false);
        onLoad?.();
      } catch (err) {
        console.error('❌ [PlaceAutocomplete] Error initializing Places API:', err);
        setIsLoading(false);
      }
    };

    initPlacesAPI();
  }, [onLoad]);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Fetch predictions using NEW API
  const fetchPredictions = useCallback(
    async (input: string) => {
      if (!input.trim() || !apiReady) {
        setPredictions([]);
        setIsOpen(false);
        return;
      }

      setIsFetching(true);

      try {
        // Build request for new API
        const request: google.maps.places.AutocompleteRequest = {
          input,
        };

        // Add included types (new API uses includedPrimaryTypes instead of types)
        if (types && types.length > 0) {
          request.includedPrimaryTypes = types;
        }

        // Add region codes (new API uses includedRegionCodes instead of componentRestrictions)
        if (countryRestriction) {
          const countries = Array.isArray(countryRestriction)
            ? countryRestriction
            : [countryRestriction];
          request.includedRegionCodes = countries;
        }

        // Use the NEW API: AutocompleteSuggestion.fetchAutocompleteSuggestions
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (suggestions && suggestions.length > 0) {
          const formattedPredictions: Prediction[] = suggestions
            .filter(suggestion => suggestion.placePrediction) // Filter out non-place suggestions
            .map((suggestion) => {
              const pred = suggestion.placePrediction!;
              return {
                placeId: pred.placeId,
                // Compose description from main + secondary text (new API doesn't have description field)
                description: pred.text?.text || '',
                mainText: pred.mainText?.text || '',
                secondaryText: pred.secondaryText?.text || '',
                types: pred.types || [],
                originalSuggestion: pred,
              };
            });

          setPredictions(formattedPredictions);
          setIsOpen(true);
          setSelectedIndex(-1);
        } else {
          setPredictions([]);
          setIsOpen(false);
        }
      } catch (err) {
        console.error('❌ [PlaceAutocomplete] Error fetching suggestions:', err);
        setPredictions([]);
        setIsOpen(false);
      } finally {
        setIsFetching(false);
      }
    },
    [apiReady, types, countryRestriction]
  );

  // Handle input change with debounce
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      onChange?.(newValue);

      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        fetchPredictions(newValue);
      }, 300);
    },
    [onChange, fetchPredictions]
  );

  // Get place details using NEW API when user selects a prediction
  const handleSelectPrediction = useCallback(
    async (prediction: Prediction) => {
      setInputValue(prediction.description);
      setIsOpen(false);
      setPredictions([]);
      onChange?.(prediction.description);

      try {
        // Use the NEW API: PlacePrediction.toPlace().fetchFields()
        const place = prediction.originalSuggestion.toPlace();
        await place.fetchFields({
          fields: ['displayName', 'formattedAddress', 'location', 'addressComponents', 'types', 'id'],
        });

        const result: PlaceAutocompleteResult = {
          formattedAddress: place.formattedAddress || prediction.description,
          displayName: place.displayName || prediction.mainText,
          location: place.location
            ? {
                lat: place.location.lat(),
                lng: place.location.lng(),
              }
            : null,
          placeId: place.id || prediction.placeId,
          types: place.types || prediction.types,
          addressComponents: place.addressComponents,
        };

        onPlaceSelect?.(result);
      } catch (err) {
        console.error('❌ [PlaceAutocomplete] Error fetching place details:', err);

        // Fallback: return basic info without detailed fields
        const fallbackResult: PlaceAutocompleteResult = {
          formattedAddress: prediction.description,
          displayName: prediction.mainText,
          location: null,
          placeId: prediction.placeId,
          types: prediction.types,
        };
        onPlaceSelect?.(fallbackResult);
      }
    },
    [onChange, onPlaceSelect]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || predictions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < predictions.length - 1 ? prev + 1 : prev
          );
          break;

        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;

        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < predictions.length) {
            handleSelectPrediction(predictions[selectedIndex]);
          }
          break;

        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setSelectedIndex(-1);
          break;

        default:
          break;
      }
    },
    [isOpen, predictions, selectedIndex, handleSelectPrediction]
  );

  // Handle clear button
  const handleClear = useCallback(() => {
    setInputValue('');
    setPredictions([]);
    setIsOpen(false);
    onChange?.('');
    inputRef.current?.focus();
  }, [onChange]);

  // Calculate dropdown position based on input position
  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4, // 4px gap
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Update position when dropdown opens or window scrolls/resizes
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();

      const handleScrollOrResize = () => {
        updateDropdownPosition();
      };

      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);

      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasValue = inputValue.length > 0;

  return (
    <div
      ref={containerRef}
      className={`place-autocomplete ${className} ${
        error ? 'place-autocomplete--error' : ''
      } ${isLoading ? 'place-autocomplete--loading' : ''} ${
        hasValue ? 'place-autocomplete--has-value' : ''
      } ${isOpen ? 'place-autocomplete--open' : ''}`}
    >
      <div className="place-autocomplete__input-wrapper">
        {/* Search icon - always visible */}
        {!isLoading && (
          <svg
            className="place-autocomplete__icon place-autocomplete__icon-search"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}

        {/* Input field */}
        <input
          ref={inputRef}
          type="text"
          className="place-autocomplete__input"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (predictions.length > 0) {
              setIsOpen(true);
            }
          }}
          disabled={disabled || isLoading}
          autoComplete="off"
          spellCheck="false"
        />

        {/* Clear button - visible when there's content */}
        {!isLoading && hasValue && !disabled && (
          <button
            type="button"
            className="place-autocomplete__icon place-autocomplete__icon-clear"
            onClick={handleClear}
            aria-label="Clear"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {/* Loading spinner */}
        {isFetching && (
          <div className="place-autocomplete__icon place-autocomplete__spinner">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="10"
                cy="10"
                r="8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="50"
                strokeDashoffset="0"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 10 10"
                  to="360 10 10"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </div>
        )}
      </div>

      {/* Predictions dropdown - rendered via portal to escape overflow:hidden */}
      {isOpen && predictions.length > 0 && createPortal(
        <div
          ref={dropdownRef}
          className="place-autocomplete__dropdown place-autocomplete__dropdown--portal"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 10001,
          }}
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.placeId}
              type="button"
              className={`place-autocomplete__item ${
                index === selectedIndex ? 'place-autocomplete__item--selected' : ''
              }`}
              onClick={() => handleSelectPrediction(prediction)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="place-autocomplete__item-icon">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 2C6.13 2 3 5.13 3 9c0 5.25 7 11 7 11s7-5.75 7-11c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S8.62 6.5 10 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div className="place-autocomplete__item-text">
                <div className="place-autocomplete__item-main">
                  {prediction.mainText}
                </div>
                {prediction.secondaryText && (
                  <div className="place-autocomplete__item-secondary">
                    {prediction.secondaryText}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Loading state fallback */}
      {isLoading && (
        <div className="place-autocomplete__loading-overlay">
          <input
            type="text"
            className="place-autocomplete__input place-autocomplete__input--loading"
            placeholder={placeholder}
            disabled
          />
        </div>
      )}

      {/* Error message */}
      {error && <span className="place-autocomplete__error">{error}</span>}
    </div>
  );
};

export default PlaceAutocomplete;
